// src/Components/FormPopup/DynamicFormDialog.jsx
import React, { useState, useRef, useEffect } from "react";

import {
  Box,
  Divider,
  Typography,
  Paper,
  Checkbox,
  Select,
  MenuItem,
  InputBase,
  styled,
} from "@mui/material";
import { LuX } from "react-icons/lu";
import { BlackButton } from "@/Components/Buttons/FiledButton";
import { OutLinedButton } from "@/Components/Buttons/OutLinedButton";
import FormGenerator from "@/Utils/FormGenerator/FormGenerator";
import CheckboxBox from "@/Components/Form/CheckboxBox";
import { LuChevronDown } from "@/Components/Icons/Icons";
import { useUser } from "@/Context/UserContext";

// Add this prop in the component signature
export default function FormDialog({
  title = "Form",
  subtitle,
  thirdTitle,
  schema,
  type = "form",
  initialData = {},
  isEditing = false,
  onClose,
  width,
  onSubmit,
  showCancelButton = true,
  showSubmitButton = true,
  cancelLabel = "Cancel",
  submitLabel,
  children,
  showCard,
  dialogWidth,
  dialogPaddingX,
  dialogPaddingY,
  boxStyles,
  handleActions,
  ref,
  disabled,
  compKey,
}) {
  const containerRef = useRef(null);
  const {
    setFormDataJson,
    isRequiredFieldFilled,
    setIsRequiredFieldFilled,
    loading,
  } = useUser();

  const CustomSelect = styled(Select)(({ theme }) => ({
    borderRadius: "10px",
    backgroundColor: "var(--gray-0)",
    border: "1px solid var(--gray-300)", // light gray border
    fontSize: "18px",
    padding: "6px 12px",
    width: "100px",
    height: "40px",
    boxShadow: "none",
    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    "& fieldset": {
      border: "none",
    },
  }));
  const [value, setValue] = useState(2);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onCloseDialog = () => {
    onClose();
  };    
  
  const handleManualSubmit = () => {
    try {
      setIsSubmitting(true);
      const scope = containerRef.current;
      const form = scope?.querySelector("form"); // only inside this dialog
      if (form?.requestSubmit) {
        form.requestSubmit();
      } else if (form) {
        form.dispatchEvent(
          new Event("submit", { cancelable: true, bubbles: true })
        );
      }
    } finally {
      setIsSubmitting(false);
      setFormDataJson({});
    }
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)", // Semi-transparent black
        zIndex: 999, // Just below your dialog
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Paper
        ref={containerRef}
        elevation={8}
        sx={{
          position: "relative",
          width: dialogWidth || 800,
          minWidth: 800,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "var(--gray-0)",
          borderRadius: 2,
          zIndex: 10000000,
          overflow: "visible",
        }}
      >
        {/* Header - Fixed Height */}
        <Box
          sx={{
            flexShrink: 0, // Prevent header from shrinking
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
          }}
        >
          <Typography fontSize="18px" fontWeight={600}>
            {title}
          </Typography>
          <LuX
            size={20}
            style={{ cursor: "pointer" }}
            onClick={onCloseDialog}
          />
        </Box>

        <Divider />

        {(subtitle || thirdTitle) && (
          <Box sx={{ px: 5, pt: 1, pb: 0 }}>
            {subtitle && (
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{ color: "text.secondary", mb: 0.5 }}
              >
                {subtitle}
              </Typography>
            )}

            {thirdTitle && (
              <Box
                sx={{
                  backgroundColor: "var(--brand-200)",
                  borderRadius: 1,
                  px: 1.5,
                  py: 0.5,
                  display: "inline-block",
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ color: "#000" }}
                >
                  {thirdTitle}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: dialogPaddingX || 4,
            py: dialogPaddingY || 1,
          }}
        >
          {type === "form" &&  (
            <FormGenerator
              key={JSON.stringify(initialData)} // ✅🔥 VERY IMPORTANT
              formName={compKey}
              schema={schema}
              initialData={initialData}
              selectedRow={initialData.id}
              onSubmit={handleActions}
              boxSx={boxStyles}
              ref={ref}
              onRequiredFieldsChange={(allFilled) =>
                setIsRequiredFieldFilled(allFilled)
              }
            />
          )}

          {children}
          {showCard && (
            <Box
              sx={{
                backgroundColor: "var(--brand-100)",
                borderRadius: "10px",
                px: 2,
                py: 1,
                mb: 2,
                mt: 2,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Typography variant="bodySmall">
                Copy From..
                <Typography
                  variant="bodySmall"
                  sx={{ ml: 1 }}
                  color="text.secondary"
                >
                  (This option allows you to copy the product details from
                  existing product.)
                </Typography>
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="bodyBase">Product No:</Typography>
                <CustomSelect
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  IconComponent={LuChevronDown}
                  input={<InputBase />}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        zIndex: 1400,
                      },
                    },
                    disablePortal: true,
                  }}
                >
                  {[1, 2, 3, 4].map((num) => (
                    <MenuItem key={num} value={num}>
                      {num}
                    </MenuItem>
                  ))}
                </CustomSelect>

                <Typography variant="bodySmall">
                  (Enter product no between 1 and 9)
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                {" "}
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CheckboxBox />

                  <label htmlFor="copy-license">
                    <Typography variant="bodySmall">Copy Licence</Typography>
                  </label>
                </Box>
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}
                >
                  <Typography
                    variant="bodyStrong"
                    sx={{ cursor: "pointer", color: "var(--link-blue)" }}
                    onClick={() => {
                      // handle reset logic
                    }}
                  >
                    Reset
                  </Typography>
                  <Typography
                    variant="bodyStrong"
                    sx={{ cursor: "pointer", color: "var(--link-blue)" }}
                    onClick={() => {
                      // handle OK logic
                    }}
                  >
                    Ok
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        <Divider />

        {/* Action Buttons */}
        {/* {(showCancelButton || showSubmitButton) && ( */}
        <Box
          display="flex"
          justifyContent="flex-end"
          gap={2}
          px={2}
          py={1}
          mt={showCancelButton || showSubmitButton ? 1 : 4}
        >
          {showCancelButton && (
            <OutLinedButton
              label={cancelLabel || "Cancel"}
              onClick={onCloseDialog}
              width="120px"
              height="40px"
            />
          )}
          {showSubmitButton && (
            <BlackButton
              label={submitLabel || (isEditing ? "Save" : "Add")}
              onClick={onSubmit}
              width="120px"
              height="40px"
              boxshadow="none"
              disabled={disabled || isSubmitting || loading}
            />
          )}
        </Box>
        {/* )} */}
      </Paper>
    </Box>
  );
}
